import { ReactElement, useContext, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  makeStyles,
} from '@fluentui/react-components';
import { CheckmarkFilled, Dismiss24Regular } from '@fluentui/react-icons';
import { Badge } from '@fluentui/react-components';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ButtonLoading, DateLabel } from '@shared/components';
import { useProductFolders } from '@modules/items';
import { AccessType } from '@modules/access';
import { SignInContext, useUsers } from '@modules/users';

import { useCreateAssessment, useUpdateAssessment } from '../../api';
import { EAssessmentDecision, EExclusionReason, EReviewingStage, TLiteratureAssessment } from '../../types';
import { getDecisionBadgeColor, getDecisionLabel } from '../../helpers';
import AssessmentFields from '../AssessmentFields';
import ProductPicker from '../ProductPicker';
import { assessmentFormSchema, TAssessmentFormData } from './assessmentDialogForm';

export type TAssessmentDialogState = {
  isOpen: boolean;
  assessment?: TLiteratureAssessment;
};

type TProps = {
  literatureId: string;
  assessments: TLiteratureAssessment[];
  state: TAssessmentDialogState;
  setState: (state: TAssessmentDialogState) => void;
};

const useStyles = makeStyles({
  dialogContent: {
    maxHeight: '70vh',
    overflowY: 'auto',
  },
});

const AssessmentDialog = ({ literatureId, assessments, state, setState }: TProps) => {
  const isEditing = !!state.assessment;
  const createAssessment = useCreateAssessment();
  const updateAssessment = useUpdateAssessment();
  const { userMap } = useUsers();
  const { user: signedInUser } = useContext(SignInContext);

  const productFolders = useProductFolders();

  const takenProductDecisions = useMemo(() => {
    const taken = new Map<string, EAssessmentDecision>();
    for (const a of assessments) {
      if (a.archived) continue;
      if (state.assessment && a._id === state.assessment._id) continue;
      for (const p of a.products) taken.set(p, a.decision);
    }
    return taken;
  }, [assessments, state.assessment]);

  const products = useMemo(
    () =>
      productFolders.map((p) => {
        // Already-assessed takes precedence over access: more informative tooltip when both apply.
        const takenDecision = takenProductDecisions.get(p._id);
        let disabledReason: string | ReactElement | undefined;
        if (takenDecision !== undefined) {
          disabledReason = (
            <span className="flex items-center gap-1">
              Assessed as
              <Badge color={getDecisionBadgeColor(takenDecision)} appearance="tint">
                {getDecisionLabel(takenDecision)}
              </Badge>
            </span>
          );
        } else if (p.accessRight < AccessType.EDIT) {
          disabledReason = 'No edit access.';
        }
        return { _id: p._id, name: p.name, disabledReason };
      }),
    [productFolders, takenProductDecisions]
  );

  const defaultValues: TAssessmentFormData = {
    products: [],
    decision: [EAssessmentDecision.PENDING],
    reviewingStage: [],
    exclusionReason: [],
    comment: '',
  };

  const {
    handleSubmit,
    control,
    reset,
    watch,
    trigger,
    formState: { errors },
  } = useForm<TAssessmentFormData>({
    resolver: yupResolver(assessmentFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  useEffect(() => {
    if (!state.isOpen) return;
    const a = state.assessment;
    reset(
      a
        ? {
            products: a.products,
            decision: [a.decision],
            reviewingStage: a.reviewingStage ? [a.reviewingStage] : [],
            exclusionReason: a.exclusionReason ? [a.exclusionReason] : [],
            comment: a.comment ?? '',
          }
        : defaultValues
    );
  }, [state.isOpen, state.assessment]);

  const onSubmit = async (data: TAssessmentFormData) => {
    const selectedProductIds = Array.from(new Set(data.products));
    const decision = data.decision[0] as EAssessmentDecision;
    // Empty string comes from the "no selection" option and is treated as cleared.
    const stage = data.reviewingStage?.[0] || undefined;
    const reviewingStage = stage as EReviewingStage | undefined;
    const exclusionReason =
      decision === EAssessmentDecision.EXCLUDED
        ? (data.exclusionReason?.[0] as EExclusionReason | undefined)
        : undefined;
    const comment = data.comment || undefined;
    const productNames = Object.fromEntries(
      selectedProductIds.map((id) => [id, products.find((p) => p._id === id)?.name ?? id])
    );

    try {
      if (isEditing && state.assessment) {
        await updateAssessment.mutateAsync({
          literatureId,
          assessmentId: state.assessment._id,
          products: selectedProductIds,
          decision,
          reviewingStage: reviewingStage ?? null,
          exclusionReason: exclusionReason ?? null,
          comment: comment ?? null,
          productNames,
        });
      } else {
        await createAssessment.mutateAsync({
          literatureId,
          products: selectedProductIds,
          decision,
          reviewingStage,
          exclusionReason,
          comment,
          productNames,
        });
      }
      setState({ ...state, isOpen: false });
    } catch {
      // error handled by mutation onError
    }
  };

  const classes = useStyles();

  return (
    <Dialog open={state.isOpen} onOpenChange={(_, data) => setState({ ...state, isOpen: data.open })}>
      <DialogSurface>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              {isEditing ? 'Edit assessment' : 'Add assessment'}
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <div className="space-y-3">
                {(() => {
                  const reviewerName = state.assessment
                    ? (userMap?.[state.assessment.reviewer]?.name ?? state.assessment.reviewer)
                    : signedInUser.name;
                  const reviewedDate = state.assessment?.reviewDate ?? new Date().toISOString();
                  return (
                    <div className="flex items-start gap-3">
                      <Field label="Reviewer" className="flex-1">
                        <Input disabled value={reviewerName} />
                      </Field>
                      <Field label="Reviewed" className="flex-1">
                        <Input disabled value={DateLabel({ children: reviewedDate })} />
                      </Field>
                    </div>
                  );
                })()}
                <Controller
                  name="products"
                  control={control}
                  render={({ field, fieldState }) => (
                    <ProductPicker
                      label="Products"
                      required
                      products={products}
                      selectedIds={(field.value as string[]) ?? []}
                      onChange={field.onChange}
                      validationMessage={fieldState.error?.message}
                    />
                  )}
                />

                <AssessmentFields control={control} errors={errors} watch={watch} trigger={trigger} />
              </div>
            </DialogContent>
            <DialogActions>
              <ButtonLoading
                type="submit"
                appearance="primary"
                icon={<CheckmarkFilled />}
                loading={createAssessment.isPending || updateAssessment.isPending}
              >
                {isEditing ? 'Update' : 'Create'}
              </ButtonLoading>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
};

export default AssessmentDialog;
